/*
 * TransactionTable.tsx (AbstractECommerce)
 *
 * Copyright © 2020 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Martin Witczak, 2020
 *
 * @file TransactionTable.tsx
 * @author Martin Witczak
 * @copyright 2020 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {Column} from 'primereact/column';
import BaseDatatable from '@abstract/abstractwebcommon-client/Table/BaseDatatable';
import DatatableColumn from '@abstract/abstractwebcommon-client/Table/DatatableColumn';
import {formatDate} from '@abstract/abstractwebcommon-shared/utils/sharedFunctions';
import TransactionTableExpansion from './TransactionTableExpansion';
import {getTransactionList, licenseCreated, regenerateInvoice, updateBillingDetails} from '../../../Store/Transactions';
import Price from '../../SubComponents/Price';
import SearchBar from '@abstract/abstractwebcommon-client/SearchBar/SearchBar';
import ActionButton from '@abstract/abstractwebcommon-client/Buttons/ActionButton';
import {useTranslation} from 'react-i18next';
import {asyncErrorHandler} from "@abstract/abstractwebcommon-shared/utils/AsyncErrorHandler";

/**
 * Interface for TransactionTable properties.
 */
interface ITransactionTableProperties {
  transactions: any;
  loading: boolean;
  handlePageUpdate: any;
  handleFilterUpdate: any;
  handleSortUpdate: any;
  createNewTransaction: any;
  skip: number;
  limit: number;
  count: number;
  sort: any;
  searchTerm: string;
}

const TransactionTable = ({
  transactions,
  loading,
  handlePageUpdate,
  handleFilterUpdate,
  handleSortUpdate,
  createNewTransaction,
  skip,
  limit,
  count,
  sort,
  searchTerm
}: ITransactionTableProperties) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [licenseUpdateLoading, setLicenseUpdateLoading] = useState(false);
  const dispatch = useDispatch();
  const regenerateInvoiceHandler = async (rowData) => {
      await asyncErrorHandler(dispatch(regenerateInvoice(rowData.customer.email, rowData._id)));
  };
  const translation = useTranslation().t;

  const markLicenseCreatedHandler = async (rowData) => {
    setLicenseUpdateLoading(true);
      await asyncErrorHandler(dispatch(licenseCreated(rowData._id)));
    setLicenseUpdateLoading(false);
      await asyncErrorHandler(dispatch(getTransactionList(skip, limit, searchTerm, sort)));
  };

  /// Handler for updating billing details
  const billingDetailsUpdateHandler = async (
    id: string,
    payload: Record<string, any>
  ) => {
      await asyncErrorHandler(dispatch(updateBillingDetails(id, payload)));
      await asyncErrorHandler(dispatch(getTransactionList(skip, limit, searchTerm, sort)));
  };

  const sortField = sort && sort.sortField;
  const sortOrder = sort && sort.sortOrder;

  /// Div containing header action buttons
  const header = (
    <div className="d-flex justify-content-between align-items-center">
      <div className="headerTableContainer">
        <ActionButton onClick={createNewTransaction} />
      </div>

      <SearchBar onSearchTermChanged={(data) => handleFilterUpdate(data)} />
    </div>
  );

  /// Triggerd on rowExpand
  const expandRow = (event: any): void => {
    if (event.data) {
      setExpandedRows({ [event.data._id]: true });
    }
  };

  return (
    <BaseDatatable
      value={transactions}
      isLoading={!transactions}
      expandedRows={expandedRows}
      header={header}
      first={skip}
      rows={limit}
      totalRecords={count}
      onPage={handlePageUpdate}
      onFilter={handleFilterUpdate}
      onSort={handleSortUpdate}
      sortField={sortField}
      sortOrder={sortOrder}
      rowsPerPageOptions={[10, 20, 50]}
      onRowExpand={expandRow}
      onRowCollapse={() => setExpandedRows({})}
      dataKey="_id"
      rowExpansionTemplate={(e) => (
        <TransactionTableExpansion
          rowData={e}
          regenerateInvoiceHandler={regenerateInvoiceHandler}
          markLicenseCreatedHandler={() => markLicenseCreatedHandler(e)}
          licenseUpdateLoading={licenseUpdateLoading}
          isAdminView={true}
          billingDetailsUpdateHandler={billingDetailsUpdateHandler}
        />
      )}
      responsive /**< Datatable responsive layout.*/
    >
      {/* Used to prevent row selection on row click */}
      <Column selectionMode="multiple" className="d-none" />
      <Column
        expander
        className="p-0 col-width-45"
        headerClassName="p-0 col-width-45"
      />
      <Column
        field="customer.customerName"
        header={translation('transaction.table.customer')}
        sortable
        body={(entry) => {
          const customerDisplayName = entry.customer
            ? `${entry.customer.fullName}`
            : '';
          const customerEmail: string = entry.customer
            ? entry.customer.email
            : '';
          return (
            <DatatableColumn
              title={translation('transaction.table.customer')}
              data={
                <>
                  <span>{customerDisplayName}</span>
                  <br></br>
                  <span>{customerEmail}</span>
                </>
              }
            />
          );
        }}
        className="col-width-250"
        headerClassName="col-width-250"
      />
      <Column
        field="username"
        header={translation('transaction.table.username')}
        className="custom-userName-width-column"
        headerClassName="custom-userName-width-column"
        sortable
        body={(transaction) => (
          <DatatableColumn
            title={translation('transaction.table.username')}
            data={transaction.usernamePassed}
          />
        )}
      />
      <Column
        sortable
        field="status"
        header={translation('transaction.table.status')}
        body={(transaction) => (
          <DatatableColumn
            title={translation('transaction.table.status')}
            data={transaction.status}
          />
        )}
        className="d-table-cell d-sm-none d-xl-table-cell col-width-250"
        headerClassName="d-table-cell d-sm-none d-xl-table-cell col-width-250"
      />
      <Column
        sortable
        header={translation('transaction.table.price')}
        field="priceCalculation.priceNet"
        className="p-col-price d-table-cell d-sm-none d-md-table-cell"
        headerClassName="p-col-price d-table-cell d-sm-none d-md-table-cell"
        body={(transaction) => (
          <DatatableColumn
            title={translation('transaction.table.price')}
            data={<Price amount={transaction.priceCalculation.priceNet} />}
          />
        )}
      />
      <Column
        sortable
        field="products.product.name"
        header={translation('transaction.table.product')}
        body={(transaction) => (
          <DatatableColumn
            title={translation('transaction.table.product')}
            data={transaction.products.map((product, index) => (
              <>
                <span key={index}>{product?.product.name}</span>
                <br></br>
              </>
            ))}
          />
        )}
        className="col-width-250"
        headerClassName="col-width-250"
      />
      <Column
        field="invoiceNo"
        header={translation('transaction.table.invoice')}
        sortable
        body={(transaction) => (
          <DatatableColumn
            title={translation('transaction.table.invoice')}
            data={transaction.invoiceNo}
          />
        )}
        className="custom-header-min-width-allowed d-table-cell d-sm-none d-xxl-table-cell"
        headerClassName="custom-header-min-width-allowed d-table-cell d-sm-none d-xxl-table-cell"
      />
      <Column
        field="transactionFulfilled"
        header={translation('transaction.table.license')}
        sortable
        className="d-table-cell d-sm-none d-xxl-table-cell custom-header-min-width-allowed"
        headerClassName="d-table-cell d-sm-none d-xxl-table-cell custom-header-min-width-allowed"
        body={(transaction) => (
          <DatatableColumn
            title={translation('transaction.table.license')}
            data={
              transaction.transactionFulfilled ? (
                <i className="pi pi-check text-success" />
              ) : (
                <i className="pi pi-times text-danger" />
              )
            }
          />
        )}
      />
      <Column
        field="failureReasonID"
        header={translation('transaction.table.recordCreatedSuccessfully')}
        sortable
        className="d-table-cell d-sm-none d-xxl-table-cell custom-header-min-width-allowed"
        headerClassName="d-table-cell d-sm-none d-xxl-table-cell custom-header-min-width-allowed"
        body={(transaction) => (
          <DatatableColumn
            title={translation('transaction.table.recordCreatedSuccessfully')}
            data={
              !transaction.failureReasonID ? (
                <i className="pi pi-check text-success" />
              ) : (
                <i className="pi pi-times text-danger" />
              )
            }
          />
        )}
      />
      <Column
        field="createdAt"
        header={translation('transaction.table.created')}
        sortable
        className="d-table-cell d-sm-none d-3xl-table-cell createdDateCol"
        headerClassName="d-table-cell d-sm-none d-3xl-table-cell createdDateCol"
        body={(transaction) => (
          <DatatableColumn
            title={translation('transaction.table.created')}
            data={
              transaction.createdAt ? formatDate(transaction.createdAt) : ''
            }
          />
        )}
      />
      <Column
        field="updatedAt"
        header={translation('transaction.table.updated')}
        sortable
        className="d-table-cell d-sm-none d-3xl-table-cell updatedDateCol"
        headerClassName="d-table-cell d-sm-none d-3xl-table-cell updatedDateCol"
        body={(transaction) => (
          <DatatableColumn
            title={translation('transaction.table.updated')}
            data={
              transaction.updatedAt ? formatDate(transaction.updatedAt) : ''
            }
          />
        )}
      />
    </BaseDatatable>
  );
};

export default TransactionTable;
